import type { BenchmarkReport } from '../../types/benchmark';
import { Tooltip } from '../ui/Tooltip';

const METRIC_TOOLTIPS: Record<string, string> = {
  'Précision': `De ce que le LLM a extrait, quel % est correct ?

Précision = matched / (matched + extra)

• matched = champs où LLM = Ground Truth
• extra = champs que le LLM a remplis mais qui sont null dans le Ground Truth

→ Une précision basse indique que le LLM "invente" des valeurs`,
  'Rappel': `De ce qui devait être extrait, quel % a été trouvé ?

Rappel = matched / (matched + missing)

• matched = champs où LLM = Ground Truth
• missing = champs présents dans le Ground Truth mais null dans l'extraction LLM

→ Un rappel bas indique que le LLM "oublie" des valeurs`,
  'Score F1': `Moyenne harmonique de Précision et Rappel

F1 = 2 × (Précision × Rappel) / (Précision + Rappel)

Le F1 équilibre précision et rappel en une seule métrique.`,
  'Score Pondéré': `Score prenant en compte l'importance des champs

Score = Σ(score × poids) / Σ(poids)

• Critique (poids 3): Champs essentiels
• Standard (poids 2): Champs importants
• Mineur (poids 1): Champs secondaires
• Exclu (poids 0): Non comptabilisés

→ Reflète mieux la qualité métier de l'extraction`
};

interface BenchmarkSummaryCardsProps {
  report: BenchmarkReport;
  onViewFirstDocument?: () => void;
}

export function BenchmarkSummaryCards({ report, onViewFirstDocument }: BenchmarkSummaryCardsProps) {
  // #583 — Tooltips métriques délégués au composant <Tooltip> partagé.
  // Calculate total time and cost per document
  const totalDocs = report.summary.total_documents;
  const totalTimeMs = Number(report.summary.avg_processing_time_ms) * totalDocs;
  const totalTimeSec = totalTimeMs / 1000;
  const avgTimeSec = Number(report.summary.avg_processing_time_ms) / 1000;

  const totalTokens = Number(report.token_usage.total_tokens);
  const avgTokensPerDoc = totalDocs > 0 ? Math.round(totalTokens / totalDocs) : 0;
  const totalCost = Number(report.token_usage.estimated_cost_usd);
  const avgCostPerDoc = totalDocs > 0 ? totalCost / totalDocs : 0;

  // Calculate document breakdown
  const perfectDocs = report.document_level.perfect_documents;
  const minorErrorDocs = report.document_level.minor_error_documents;
  const criticalErrorDocs = report.document_level.critical_error_documents;

  const cards = [
    {
      title: 'Documents',
      value: totalDocs.toString(),
      description: `${perfectDocs} parfaits, ${minorErrorDocs + criticalErrorDocs} erreurs`,
      color: 'bg-gray-50 border-gray-200',
    },
    {
      title: 'Précision',
      value: `${(Number(report.summary.avg_precision) * 100).toFixed(1)}%`,
      description: 'Moyenne',
      color: 'bg-blue-50 border-blue-200',
    },
    {
      title: 'Rappel',
      value: `${(Number(report.summary.avg_recall) * 100).toFixed(1)}%`,
      description: 'Moyenne',
      color: 'bg-purple-50 border-purple-200',
    },
    {
      title: 'Score F1',
      value: `${(Number(report.summary.avg_f1) * 100).toFixed(1)}%`,
      description: 'Harmonique',
      color: 'bg-green-50 border-green-200',
    },
    ...(report.weighted_score ? [{
      title: 'Score Pondéré',
      value: `${(report.weighted_score.final_score * 100).toFixed(1)}%`,
      description: `${Math.round(report.weighted_score.avg_fields_scored || 0)} champs scorés`,
      color: 'bg-indigo-50 border-indigo-200',
    }] : []),
    {
      title: 'Temps',
      value: totalTimeSec >= 60
        ? `${(totalTimeSec / 60).toFixed(1)}m`
        : `${totalTimeSec.toFixed(1)}s`,
      description: `${avgTimeSec.toFixed(1)}s/doc`,
      color: 'bg-yellow-50 border-yellow-200',
    },
    {
      title: 'Tokens',
      value: `${(totalTokens / 1000).toFixed(1)}k ($${totalCost.toFixed(3)})`,
      description: `${avgTokensPerDoc} tokens/doc ($${avgCostPerDoc.toFixed(4)})`,
      color: 'bg-pink-50 border-pink-200',
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
        {cards.map((card) => (
          <div
            key={card.title}
            className={`p-3 border rounded-lg ${card.color} ${
              card.title === 'Documents' && onViewFirstDocument
                ? 'cursor-pointer hover:shadow-md transition-shadow'
                : ''
            }`}
            onClick={() => card.title === 'Documents' && onViewFirstDocument && onViewFirstDocument()}
          >
            <div className="text-sm font-medium text-gray-600 mb-1 flex items-center">
              {card.title}
              {METRIC_TOOLTIPS[card.title] && (
                <Tooltip
                  content={METRIC_TOOLTIPS[card.title]}
                  heading={card.title}
                  maxWidth="max-w-md"
                  className="text-blue-600 ml-1"
                  ariaLabel={`Aide ${card.title}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              )}
            </div>
            <div className="text-2xl font-bold text-gray-900">{card.value}</div>
            <div className="text-xs text-gray-500 mt-1">{card.description}</div>
          </div>
        ))}
      </div>

      {/* #583 — Tooltip délégué au composant <Tooltip> partagé (rendu inline avec le déclencheur). */}
    </>
  );
}
